import ReactGA from 'react-ga4';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ampli } from '../../../../../ampli';
import { Button } from '../../../../../components/common/buttons/Button';
import { Tooltip } from '../../../../../components/common/tooltip/Tooltip';
import { pageInfo } from '../../../../../constants/pageInfo/pageInfo.constant';
import { usePostUserSellTractorInquiries } from '../../../../../hooks/api/inquiry/mutations/usePostUserSellTractorInquiries';
import { usePopup } from '../../../../../hooks/popup/usePopup';
import { useCheckUserLoggedIn } from '../../../../../hooks/user/useCheckUserLoggedIn';
import { useGetUserProfile } from '../../../../../hooks/user/useGetUserProfile';
import { IOpenMarketProductDetail } from '../../../../../interfaces/OpenMarketProduct.interfaces';
import { postMessageToRN } from '../../../../../utils/RNCommunicator/postMessageToRN.util';
import { getOpenMarketProductTitle } from '../../../../../utils/getter/getOpenMarketProductTitle.util';

interface DirectDealRequestConsultButtonProps {
  product: IOpenMarketProductDetail;
  onPriceSuggestClick?: () => void;
  handleBumpButtonClick: (product: IOpenMarketProductDetail) => void;
}

export const DirectDealRequestConsultButton = ({
  product,
  onPriceSuggestClick,
  handleBumpButtonClick,
}: DirectDealRequestConsultButtonProps) => {
  const Popup = usePopup();
  const navigate = useNavigate();
  const location = useLocation();
  const { isUserLoggedIn } = useCheckUserLoggedIn();
  const { userProfile } = useGetUserProfile();

  const { mutate: postUserSellTractorInquiries } = usePostUserSellTractorInquiries();

  const isMyProduct = userProfile?.id === product?.user?.id;

  const handlePriceSuggestButtonClick = () => {
    onPriceSuggestClick?.();
  };

  const handleRequestConsultButtonClick = () => {
    if (!product) {
      return;
    }

    ampli.마켓오픈마켓매물전화상담버튼클릭({
      '게시글 제목':
        getOpenMarketProductTitle({ brandName: product.machine?.brand?.name, machine: product.machine }) ?? '',
      '매물 코드': product?.code,
    });

    postUserSellTractorInquiries({ reqBody: { userSellTractorId: product.id } });

    ampli.pvmProductInquiry({
      title: getOpenMarketProductTitle({ brandName: product.machine?.brand?.name, machine: product.machine }) ?? '',
      type: 'MemberToMember',
      page_type: 'odp',
      ps: product.machine.engineOutputInPs,
      manufacturedYear: product.manufacturedYear,
      operationTime: product.operationTime,
    });
    ReactGA.event('pvm_product_inquiry', {
      category: 'pvm',
      action: 'pvm_product_inquiry',
      value: 'MemberToMember',
    });

    if (window.ReactNativeWebView) {
      postMessageToRN({ messageKey: 'link', messageParam: `tel:${product.user?.phoneNumber}` });
    } else {
      window.location.href = `tel:${product.user?.phoneNumber}`;
    }
  };

  if (!product) {
    return null;
  }

  return (
    <ButtonContainer>
      {!!product.priceNegotiationYn && !isMyProduct && (
        <ButtonWrapper>
          <Tooltip
            type={'default'}
            size={'large'}
            customColor={{ background: '#505363', text: '#FFFFFF' }}
            text={'문자로 찔러보기 해보세요 💌'}
            style={{ left: 'initial' }}
          >
            <ButtonWrapper>
              <Button
                borderType={'square'}
                color={'filledPrimaryLight'}
                size={'lg'}
                onClick={handlePriceSuggestButtonClick}
                icon={{
                  fileName: 'Envelope',
                  size: 'Large',
                  fill: '#445EFF',
                  position: 'left',
                }}
                text={{
                  content: '찔러보기',
                  size: 'lg',
                }}
              />
            </ButtonWrapper>
          </Tooltip>
        </ButtonWrapper>
      )}
      {isMyProduct && (
        <ButtonWrapper>
          <Tooltip
            type={'default'}
            size={'large'}
            customColor={{ background: '#505363', text: '#FFFFFF' }}
            text={'상단에 재등록 🔥'}
            style={{ left: 'initial' }}
          >
            <ButtonWrapper onClick={() => handleBumpButtonClick(product)}>
              <Button
                borderType={'square'}
                color={'filledGreyLight'}
                size={'lg'}
                text={{
                  content: '끌어올리기',
                  size: 'lg',
                }}
                onClick={() => handleBumpButtonClick(product)}
              />
            </ButtonWrapper>
          </Tooltip>
        </ButtonWrapper>
      )}
      {isMyProduct && (
        <ButtonWrapper>
          <Button
            borderType={'square'}
            color={'filledPrimaryStrong'}
            size={'lg'}
            onClick={() => navigate(pageInfo.myInfoSellTractorListPage.path)}
            text={{
              content: '매물 수정하기',
              size: 'lg',
            }}
          />
        </ButtonWrapper>
      )}
      {!isMyProduct && (
        <ButtonWrapper>
          <Button
            borderType={'square'}
            color={'filledPrimaryStrong'}
            size={'lg'}
            onClick={handleRequestConsultButtonClick}
            icon={{
              fileName: 'Phone',
              size: 'Large',
              fill: '#fff',
              position: 'left',
            }}
            text={{
              content: '판매자 문의',
              size: 'lg',
            }}
          />
        </ButtonWrapper>
      )}
    </ButtonContainer>
  );
};

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 0 0;
`;
